import { useSelector } from "react-redux";
import type { IRootReducerState } from "../../../../store/IRootReducer";
import { useGetMyPermissionsQuery } from "../../../../store/api/rbacApi";
import { useCurrentUser } from "./useCurrentUser";

const ROLE_LABELS: Record<string, string> = {
  skypoint_admin: "Skypoint Admin",
  skysupport_admin: "skySupport Administrator",
  skysupport_manager: "skySupport Manager",
  skysupport_user: "skySupport User",
};

export interface AccountRoleInputs {
  /** my-permissions' role — the authoritative source (handles skypoint_admin). */
  rbacRole?: string | null;
  rbacRoleLabel?: string | null;
  /** Set during app initialization from resolve-instance. */
  resolvedRole?: string | null;
  isPlatformAdmin?: boolean;
  isTenantAdmin?: boolean;
  signedIn?: boolean;
}

/**
 * The signed-in person's role on this instance, as a label. One derivation for
 * the account panel and the account page — the page used to say "Member" for
 * anyone but a platform admin while the panel named their real role (#1046).
 */
export function accountRoleLabel(inputs: AccountRoleInputs): string | null {
  const role = inputs.rbacRole || inputs.resolvedRole;
  if (role) return ROLE_LABELS[role] || inputs.rbacRoleLabel || role;
  if (inputs.isPlatformAdmin) return ROLE_LABELS.skysupport_admin;
  if (inputs.isTenantAdmin) return ROLE_LABELS.skysupport_manager;
  // Signed in but no role resolved yet — default to user.
  if (inputs.signedIn) return ROLE_LABELS.skysupport_user;
  return null;
}

export function useAccountRoleLabel(): string | null {
  const { currentUser } = useCurrentUser();
  const resolvedRole = useSelector((state: IRootReducerState) => state.authReducer?.resolvedRole);
  const permissions = useSelector((state: IRootReducerState) => state.authReducer?.permissions);
  const { data: rbacPerms } = useGetMyPermissionsQuery();
  return accountRoleLabel({
    rbacRole: rbacPerms?.role,
    rbacRoleLabel: rbacPerms?.roleLabel,
    resolvedRole,
    isPlatformAdmin: permissions?.isPlatformAdmin,
    isTenantAdmin: permissions?.isTenantAdmin,
    signedIn: !!currentUser,
  });
}
